import { FC } from 'react';
import { css, keyframes } from '@emotion/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import remend from 'remend';
import 'katex/dist/katex.min.css';
import { colors, fontStyles, spacing } from '../styles';
import { cleanMarkdownContent, stripIncompleteMathFences } from '../utils/contentCleaning';
interface LoadingMessageProps {
  progressText: string;
  streamingMessage?: string;
  textSize?: 'small' | 'normal';
}

const pulseAnimation = keyframes`
  0%, 20% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
  80%, 100% {
    opacity: 0.3;
  }
`;

// Sprint 3c: Breathing animation for progress text
const breatheAnimation = keyframes`
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
`;

const LoadingMessage: FC<LoadingMessageProps> = ({ progressText, streamingMessage, textSize = 'small' }) => {
  const textStyle = textSize === 'normal' ? fontStyles.dog : fontStyles.thinFlea;
  // If we have streaming content, show it with a blinking cursor
  if (streamingMessage) {
    return (
      <div
        css={css`
          width: 100%;
          ${textStyle}
          color: ${colors.liquorice};
          text-align: left;
          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }

          /* Markdown styles to match ChatPage */
          & p {
            margin: 0 0 ${spacing.deci} 0;
            text-align: left;
            &:last-child {
              margin-bottom: 0;
            }
          }
          & h1 {
            ${fontStyles.lion}
            margin: 0 0 ${spacing.centi} 0;
            text-align: left;
          }
          & h2 {
            ${fontStyles.dog}
            margin: 0 0 ${spacing.milli} 0;
            text-align: left;
          }
          & ul,
          & ol {
            margin: ${spacing.milli} 0;
            padding-left: ${spacing.regular};
            text-align: left;
          }
          & li {
            margin: ${spacing.milli} 0;
            text-align: left;
          }
          & code {
            background-color: rgba(0, 0, 0, 0.1);
            padding: 2px 4px;
            border-radius: 4px;
          }
          & pre {
            background-color: rgba(0, 0, 0, 0.1);
            padding: ${spacing.milli};
            border-radius: 4px;
            overflow-x: auto;
            margin: ${spacing.milli} 0;
          }
          & strong {
            font-weight: 500;
          }

          & a {
            color: ${colors.petrolBlue};
            html[data-theme='dark'] & {
              color: ${colors.skyBlue};
            }
          }

          /* Table styles */
          & table {
            border-collapse: collapse;
            margin: ${spacing.deci} 0;
            width: 100%;
            display: block;
            overflow-x: auto;
            text-align: left;
          }

          & th,
          & td {
            border: 1px solid ${colors.cloud};
            padding: ${spacing.milli};
            text-align: left;
          }

          & th {
            background-color: rgba(0, 0, 0, 0.1);
            font-weight: 600;
            text-align: left;
          }
        `}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: false }]]}
          rehypePlugins={[rehypeRaw as any, rehypeKatex as any]}
        >
          {cleanMarkdownContent(remend(stripIncompleteMathFences(streamingMessage), { katex: false }))}
        </ReactMarkdown>
        <span
          css={css`
            animation: blink 1s infinite;
            @keyframes blink {
              0%,
              50% {
                opacity: 1;
              }
              51%,
              100% {
                opacity: 0;
              }
            }
          `}
        >
          ▌
        </span>
      </div>
    );
  }

  // Otherwise show progress with animated dots and tool outputs
  return (
    <div
      css={css`
        width: 100%;
        ${textStyle}
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: ${spacing.milli};
        color: ${colors.liquorice};
        html[data-theme='dark'] & {
          color: ${colors.sugar};
        }
      `}
    >
      <div
        css={css`
          display: flex;
          gap: 2px;
          align-items: center;
        `}
      >
        <div
          css={css`
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background-color: ${colors.petrolBlue};
            animation: ${pulseAnimation} 1.4s infinite ease-in-out;
            animation-delay: 0s;
            html[data-theme='dark'] & {
              background-color: ${colors.skyBlue};
            }
          `}
        />
        <div
          css={css`
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background-color: ${colors.petrolBlue};
            animation: ${pulseAnimation} 1.4s infinite ease-in-out;
            animation-delay: 0.2s;
            html[data-theme='dark'] & {
              background-color: ${colors.skyBlue};
            }
          `}
        />
        <div
          css={css`
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background-color: ${colors.petrolBlue};
            animation: ${pulseAnimation} 1.4s infinite ease-in-out;
            animation-delay: 0.4s;
            html[data-theme='dark'] & {
              background-color: ${colors.skyBlue};
            }
          `}
        />
      </div>
      <span
        css={css`
          animation: ${breatheAnimation} 2s infinite ease-in-out;
        `}
      >
        {progressText}
      </span>
    </div>
  );
};

export default LoadingMessage;
